import { Camera, RecordingSegmentMetadata, RecordingType } from '../../../api/models'
import { RecordingSegment, RecordingSegments } from '../../../api/apiQueries'
import { DateTime } from 'luxon'
import { customEvent, eventWithTime } from '@rrweb/types'
import pako from 'pako'
import { RRWEB_EVENTS } from '@simplisafe/compass-web'
import { LoggerName, namedLogger } from '../../../utils/logger'
import { formatIsoTimestamp } from '../../../utils/dateUtils'

const logger = namedLogger(LoggerName.ertUtils)

const missingMetadataId = 'no recordingId provided'

export type ValidatedRecording = Required<Pick<RecordingSegmentMetadata, 'recordingId' | 'monitoringSpecialistId'>> & {
  // This is the start of the recording in relation to the overall ERT recording. If the specialist audio starts 10 seconds in then the value would be 10000
  relativeStartTimeSeconds: number
  segments: RecordingSegment[]
  recordingType: RecordingType
  error?: string
  errorDescription?: string
}

function getRelativeStartTimeSeconds(recordingStartTime: string | undefined, timeOfFirstEvent: number) {
  if (timeOfFirstEvent === 0) {
    return 0
  }

  if (recordingStartTime) {
    const recordingStartTimeEpoch = new Date(recordingStartTime).getTime()
    const relativeStartTimeMillis = recordingStartTimeEpoch - timeOfFirstEvent
    return relativeStartTimeMillis / 1000
  }

  return 0
}

/**
 * create an array of AudioRecordings from a list of RecordingSegments
 * the segments should be divided by recordingId and sorted by sequenceNumber and then placed in an AudioRecording object
 * if any segments do not have a recordingId, they should be placed in a AudioRecordingShell in the order they were received
 *
 * @param recordingSegments
 * @param timeOfFirstEvent
 * @param webEventFilter use this to filter web events right after each segment is parsed.
 * Ert Inspect uses this to drop data that it doesn't need but would otherwise live in memory
 * and could potentially grow large enough to crash the browser tab
 * @returns array of ValidatedRecording
 */
export const assembleAndValidateRecordingsFromSegments = async (
  recordingSegments: RecordingSegments,
  timeOfFirstEvent = 0,
  webEventFilter?: (event: eventWithTime) => boolean
): Promise<ValidatedRecording[]> => {
  const recordingsMap: Map<string, ValidatedRecording> = new Map()
  const validatedRecordings: ValidatedRecording[] = []

  // group segments by recordingId
  for (const segment of recordingSegments.segments) {
    const recordingId =
      !segment.recordingId || !segment.recordingStartTime || segment.sequenceNumber === undefined ? missingMetadataId : segment.recordingId
    let recording: ValidatedRecording | undefined = recordingsMap.get(recordingId)
    if (!recording) {
      recording = {
        recordingId: recordingId,
        relativeStartTimeSeconds: getRelativeStartTimeSeconds(segment.recordingStartTime, timeOfFirstEvent),
        monitoringSpecialistId: segment.monitoringSpecialistId,
        recordingType: recordingSegments.recordingType,
        segments: []
      }
      recordingsMap.set(recordingId, recording!)
    }

    // The parsed events go onto a COPY of the segment, never the segment itself: these objects live in
    // the React Query cache and are shared across assembly passes. QA (unfiltered) and Inspect (video
    // events only) can interleave on a mode switch, and writing onto the shared segment would let the
    // last writer poison the other pipeline's read.
    let segmentForRecording = segment
    if (recording.recordingType === RecordingType.web) {
      const arrayBuffer = await segment.data.arrayBuffer()
      const reconstitutedBytes = pako.inflate(arrayBuffer)
      const reconstitutedJsonStr = new TextDecoder().decode(reconstitutedBytes)
      const events = JSON.parse(reconstitutedJsonStr) as eventWithTime[]
      const filteredEvents = webEventFilter ? events.filter(webEventFilter) : events
      segmentForRecording = { ...segment, rrwebEvents: filteredEvents }
      logger.debug(`Segment ${segment.segmentId} contains ${events.length} events`, { console: { events: filteredEvents } })
    }

    recording.segments.push(segmentForRecording)
  }

  // sort and validate segments for each recording by sequenceNumber if the metadata to do so is present
  for (const [recordingId, recording] of recordingsMap.entries()) {
    if (recordingId !== missingMetadataId) {
      recording.segments.sort((a, b) => a.sequenceNumber! - b.sequenceNumber!)

      // validate that all segments in a recording are present
      const missingSegments: number[] = []
      let sequenceNumber = 0
      for (const segment of recording.segments) {
        while (sequenceNumber < (segment.sequenceNumber ?? 0)) {
          missingSegments.push(sequenceNumber)
          sequenceNumber++
        }

        sequenceNumber++
      }
      if (missingSegments.length > 0) {
        recording.error = `Part of this ${recordingSegments.recordingType} recording is missing. Review with caution.`
        recording.errorDescription = `${Math.round((missingSegments.length / ((recording.segments[recording.segments.length - 1].sequenceNumber ?? 0) + 1)) * 100)}% of this ${recordingSegments.recordingType} recording is missing. Missing sequence numbers: [${missingSegments.join(', ')}]`
        if (missingSegments.includes(0) && recording.recordingType === RecordingType.audio) {
          // if the first audio segment is missing, the audio won't play at all, so we need to add a dummy segment with the opus audio headers in it.
          const secondSegment = recording.segments[0]
          // create a dummy segment based on the information from the second segment.
          const dummySegment = getDummyFirstSpecialistAudioSegment(secondSegment)
          // add the dummy segment to the beginning of the segments array for this recording
          recording.segments.splice(0, 0, dummySegment)
        }
      }
    } else {
      recording.segments.sort((a, b) => DateTime.fromISO(a.timestamp).toMillis() - DateTime.fromISO(b.timestamp).toMillis())
      const histogram: Record<number, number> = {}
      let lastTimestamp = DateTime.fromISO(recording.segments[0].timestamp).toMillis()
      for (let i = 1; i < recording.segments.length; i++) {
        const currentTimestamp = DateTime.fromISO(recording.segments[i].timestamp).toMillis()
        const differenceMillis = currentTimestamp - lastTimestamp
        const differenceSeconds = Math.round(differenceMillis / 1000)
        histogram[differenceSeconds] = (histogram[differenceSeconds] ?? 0) + 1
        lastTimestamp = currentTimestamp
      }
      let medianTimeDiff = 0
      const entries = Object.entries(histogram)
      entries.sort((a, b) => b[1] - a[1])
      for (const entry of entries) {
        const timeDiff = parseInt(entry[0])
        if (timeDiff > 0) {
          medianTimeDiff = timeDiff
          break
        }
      }

      lastTimestamp = DateTime.fromISO(recording.segments[0].timestamp).toMillis()
      let lastSegment = recording.segments[0]
      let lastEventTag: string | null = null
      let currentEventTag: string | null = null
      let missingCount = 0
      for (let i = 1; i < recording.segments.length; i++) {
        const currentSegment = recording.segments[i]
        const currentTimestamp = DateTime.fromISO(currentSegment.timestamp).toMillis()
        const differenceMillis = currentTimestamp - lastTimestamp
        const differenceSeconds = Math.round(differenceMillis / 1000)
        const segmentsElapsed = Math.round(differenceSeconds / medianTimeDiff)
        lastEventTag = null
        currentEventTag = null
        if (recording.recordingType === RecordingType.web && lastSegment.rrwebEvents?.length && currentSegment.rrwebEvents?.length) {
          lastEventTag = (lastSegment.rrwebEvents[lastSegment.rrwebEvents.length - 1] as customEvent).data.tag
          currentEventTag = (currentSegment.rrwebEvents[0] as customEvent).data.tag
        }
        if (segmentsElapsed > 1) {
          if (lastEventTag === RRWEB_EVENTS.RECORDING_STOPPED && currentEventTag === RRWEB_EVENTS.RECORDING_STARTED) {
            // this means that we are not actually missing segments because the recording was just abandoned
          } else {
            missingCount += segmentsElapsed - 1
          }
        }
        lastTimestamp = currentTimestamp
        lastSegment = currentSegment
      }

      if (missingCount > 0) {
        recording.error = `Part of this ${recordingSegments.recordingType} recording may be missing. Review with caution.`
        recording.errorDescription = `Segments in this ${recordingSegments.recordingType} recording are ${medianTimeDiff} seconds apart. ${missingCount} segments not found at the expected timestamped intervals.`
      }
    }

    validatedRecordings.push(recording)
  }

  logger.debug(`Recordings (${recordingSegments.recordingType}):`, { console: { validatedRecordings } })

  return validatedRecordings
}

export type LiveStreamCapture = Required<Pick<RecordingSegmentMetadata, 'recordingId' | 'monitoringSpecialistId'>> & {
  blobUrl: string
}
type LiveStreamCaptureShell = Omit<LiveStreamCapture, 'blobUrl'> & {
  segments: RecordingSegment[]
}

/**
 * @deprecated - this is part of temporary solution to test out live stream recordings that will be replaced.
 * create an array of VideoRecording from a list of RecordingSegments
 * the segments should be divided by recordingId and sorted by sequenceNumber and then placed in an VideoRecording object
 *
 * Note: the blobUrl created here should be revoked when no longer needed using URL.revokeObjectURL
 *
 * @param recordingSegments
 * @returns map of VideoRecordings by recordingId
 */
export const createObjectUrlsForLiveStreamCaptures = (recordingSegments: RecordingSegments): Map<string, LiveStreamCapture> => {
  const recordingsMap: Map<string, LiveStreamCaptureShell> = new Map()
  const recordingShells: LiveStreamCaptureShell[] = []

  // group segments by recordingId
  for (const segment of recordingSegments.segments) {
    const recordingId =
      !segment.recordingId || !segment.recordingStartTime || segment.sequenceNumber === undefined ? missingMetadataId : segment.recordingId
    let recording: LiveStreamCaptureShell | undefined = recordingsMap.get(recordingId)
    if (!recording) {
      recording = {
        recordingId: recordingId,
        monitoringSpecialistId: segment.monitoringSpecialistId,
        segments: []
      }
      recordingsMap.set(recordingId, recording!)
    }
    recording.segments.push(segment)
  }

  // sort segments for each recording by sequenceNumber if the metadata to do so is present
  for (const [recordingId, recording] of recordingsMap.entries()) {
    if (recordingId !== missingMetadataId) {
      recording.segments.sort((a, b) => a.sequenceNumber! - b.sequenceNumber!)
    }
    recordingShells.push(recording)
  }

  // concatenate segments for each recording shell into a single blob and create VideoRecording objects with the data attribute referencing the blob
  const videoRecordings: Map<string, LiveStreamCapture> = new Map()
  for (const recordingShell of recordingShells) {
    const segments = []

    for (const segmentDetail of recordingShell.segments) {
      segments.push(segmentDetail.data)
    }

    const data = new Blob(segments, { type: 'video/webm' })
    // now that we have all the segments in a single array, create the blob
    videoRecordings.set(recordingShell.recordingId, {
      ...recordingShell,
      blobUrl: URL.createObjectURL(data)
    })
  }

  return videoRecordings
}

/**
 * This is used to repair specialist audio recordings that are missing the first segment.
 * @param secondSegment
 */
function getDummyFirstSpecialistAudioSegment(secondSegment: RecordingSegment): RecordingSegment {
  // this was created by taking the first 125 millis of blank audio captured from holodeck and base64 encoding the results. This has the opus headers we need for the rest of the audio to be decoded.
  const dummyFirstSegment =
    'GkXfo59ChoEBQveBAULygQRC84EIQoKEd2VibUKHgQRChYECGFOAZwH/////////FUmpZpkq17GDD0JATYCGQ2hyb21lV0GGQ2hyb21lFlSua7+uvdeBAXPFhwfOd7rOOBWDgQKGhkFfT1BVU2Oik09wdXNIZWFkAQEAAIC7AAAAAADhjbWERzuAAJ+BAWJkgSAfQ7Z1Af/////////ngQCjQ8OBAACA+wOKni2e13bC2OuOZSmzYwD69HZgdcot8/0CTjObI01cqKayX6UAjEmtXPxvA4ke80zBAvbx0ipwf7K3Wz8/kLKg+5pR98XLkzuwgLsEXY9Ih+PK9Cb5Wu/pQBzsgdpqUo5QigYLX3D3qbgURmmPvIkoOt7fUMrPvFolRBQnu83M3Y/3TLm19vx2Y99xoMPhhbhbvJ/eiSli6myAvKxu7hLQJI9qpw2JxpNq+XXAP8oywWolUSa1wjrvJeXMQ6YOp+OGfeFJYyCqp/OJKDbJ7HLrqGt8VPUR1tJMdSqBwaTjUnqfATA/RlIgpEpVl8JXs1AAkuA3o9/EQQb7jlQ+YHPAMxiKepHweP2Wg2e5yH+dc7zmGwez3WGXecess1wUWxZndNN4QqGA3R3QiKuqpulpWIvh1Z8yZSNcFsAb+BDQh5mMd7ax0vcRHSpFRNFh9sad7PGk0st9uUcmSrafnKGT4Ckw8YxT1Ss14M4jJk/IhY8D7QmUIzQ3OUCOTNZcrmJ6CHatBZ8lpiahKuT719nsJ2rOTCHfqeiAlly3VXjy2GnvYAwSMbitZh9Y4kgKNTYL27m0XWw1RuN+RZNAKMppKpNeAtWctzigrhhn1+Ai5Tv1liweaEVDBcZd3ixB9F+yQI5XvXt5RMWrW+YUTqVpxUX8d8r5QHOMdybeXOS+eXCXA43+kkZtc0gw+iOOdki6Cv1iiN2nPnPjsS5adyLyAtJ6pE9XpVrEbBKMdtxY6F92h62QflLP2mxGZHvRWcD8PZYkh0DURi/ZKRGcj2+5l/9WLy3LYZfHy8kYsT6zwHA/wxGtJC7ptiZhe8p5yEreJtSFiw3KYPXcfGImUFO5DgERy06LjOOp9qLfsqHs7Kp4EqE/muNK2TSRCXkU9vAASSph2CKCV9n7+T0vsfhBcWtE9xibIbOEwZQkm8xrvmfX1riyQVs4X59mHKefNkGzrHDB3XH/OZpZrvw2bxUMr4QgOutu0dWQztl3p/lXaluCuoEFDVigLIpyDT8Jdn/+wXCl8gcTwG/Hy9fOG8uOEg/ByEyNa6NIa2agG9fP1qcC80tsWUhMeE8T+f9jvHF+j6pyaM5MY1ybgSoKA/ZTa1vAu8AZgteyesdTj0XiP2OpIbDlb8EEfbJmGWYas9NWVu7MYte8GRbsdV/Kv47F12irXPbtbV8ovUb8iIqtmHyI+uMR0Et0C4WOM8IubhsCX3YZdaM8YN/7muRqAtkz2mjbIZX+7b+xEqaQRevNhqVmkKQT7wfGnjbkmnKj'
  // Decode the Base64 string into a binary string
  const binaryString = atob(dummyFirstSegment)
  const len = binaryString.length
  // Create an ArrayBuffer and a Uint8Array to hold the binary data
  const bytes = new Uint8Array(len)

  // Populate the Uint8Array with the byte values from the binary string
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // Create the Blob object with the Uint8Array and the specified MIME type
  const dummyData = new Blob([bytes], { type: 'audio/webm' })

  return {
    recordingId: secondSegment.recordingId,
    segmentId: `dummy-first-segment-${secondSegment.recordingId}`,
    sessionId: secondSegment.sessionId,
    engagementIds: secondSegment.engagementIds,
    recordingStartTime: secondSegment.recordingStartTime,
    timestamp: secondSegment.timestamp,
    sequenceNumber: 0,
    monitoringSpecialistId: secondSegment.monitoringSpecialistId,
    data: dummyData
  } satisfies RecordingSegment
}

export function findCameraName(cameraId: string | undefined, cameras: Camera[] | undefined): string | undefined {
  return cameras?.find(camera => camera.uuid === cameraId)?.name
}

export const truncatedEngagementId = (engagementId: string): string => {
  // get the last 12 characters of the engagement ID for display
  return `...${engagementId.slice(-12)}`
}

/**
 *
 * @param timestamp
 * @param timezone if timezone is missing it will default to the current user's (browser) timezone
 * @returns formatted date string
 */
export const formatEngagementDate = (timestamp: string, timezone?: string) =>
  DateTime.fromISO(timestamp, { zone: timezone }).toFormat("MM/dd/yyyy 'at' h:mma ZZZZ")

/**
 *
 * @param timestamp
 * @param timezone if timezone is missing it will default to the current user's (browser) timezone
 * @returns bare clock time, e.g. "9:08 PM"
 */
export const formatTimelineTime = (timestamp: string, timezone?: string) => formatIsoTimestamp(timestamp, { zone: timezone, format: 'h:mm a' })
